/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/camelcase */
import React, { useEffect, useState } from "react";

import {
  AcceptedFileType,
  AddressType,
  bills,
  DocumentReferenceType,
  fileMaxSize,
  fileNameSize,
  FreightMode,
  IncotermPoint,
  PageType,
  PortType,
  ShippingType,
  ShippingTypeValue,
} from "@assets/constants";
import { difference } from "@assets/helperFunctions";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import {
  BillOfLading,
  AddCargoData,
  CompanyAddress,
  PickPartnerChangeRegistration,
  UserType,
  Document,
  Port,
  Incoterms,
  BookingRequest,
  RequestCollectionRegistration,
  SelectOption,
  ErrorTypes,
  DocumentError,
  HSCodes,
  BookingDetail,
  RouteBookingResponse,
  CargoBookingResponse,
  BookingDimension,
  UploadDocumentsResponse,
} from "@assets/types";
import { ArrayUtilities, IncotermUtilities } from "@assets/utilities";
import HeaderPage from "@components/Page/headerPage";
import Page from "@components/Page/page";
import Stepper from "@components/Stepper/stepper";
import CreateBookingService from "@services/CreateBookingService";
import SingleBookingService from "@services/SingleBookingService";
import { getBookingCargo } from "@state/slices/cargoBookingSlice";
import { getIncoterms, incotermsState } from "@state/slices/incotermsSlice";
import {
  getCompanyAddresses,
  getCompanyCustomerAddresses,
  getPartnerAddress,
  partnerState,
} from "@state/slices/partnerSlice";
import { getPorts, portsState } from "@state/slices/portsSlice";
import { getRouteBooking } from "@state/slices/routeBookingSlice";
import { getShippingTypes, shippingTypesState } from "@state/slices/shippingTypesSlice";
import { getUserProfileFromAPI, userState } from "@state/slices/userSlice";
import { navigate } from "gatsby";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

import AddDocumentsComponent from "./addDocumentsComponent";
import CargoInformationComponent from "./cargoInformationComponent";
import ReviewComponent from "./reviewComponent";
import RouteComponent from "./routeComponent";

interface CreateTraceProps {
  // eslint-disable-next-line react/no-unused-prop-types
  path?: string;
  freightMode: FreightMode;
  pageType?: PageType;
  jobReference?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  iframeUpdater: number;
  // eslint-disable-next-line react/no-unused-prop-types
  resetBookingState: Function;
}

const CreateTrace: React.FC<CreateTraceProps> = (props: CreateTraceProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const portsAll = useAppSelector(portsState);
  const userProfile = useAppSelector(userState).userProfile;
  const loadingCompanyAddresses = useAppSelector(partnerState).loadingCompanyAddresses;
  const companyCustomerAddresses = useAppSelector(partnerState).companyCustomerAddresses;
  const loadingCompanyCustomerAddresses =
    useAppSelector(partnerState).loadingCompanyCustomerAddresses;
  const incoterms = useAppSelector(incotermsState).incoterms;
  const loadingIncoterms = useAppSelector(incotermsState).loadingIncoterms;
  const shippingTypes = useAppSelector(shippingTypesState).shippingTypes;

  const [shipperData, setShipperData] = useState<PickPartnerChangeRegistration | null>(null);
  const [consigneeData, setConsigneeData] = useState<PickPartnerChangeRegistration | null>(null);
  const [notifierData, setNotifierData] = useState<PickPartnerChangeRegistration | null>(null);
  const [sellerData, setSellerData] = useState<PickPartnerChangeRegistration | null>(null);
  const [buyerData, setBuyerData] = useState<PickPartnerChangeRegistration | null>(null);
  const [portsOfLoad, setPortsOfLoad] = useState<Port[] | null>(null);
  const [portsOfDischarge, setPortsOfDischarge] = useState<Port[] | null>(null);
  const [portOfLoad, setPortOfLoad] = useState<Port | null>(null);
  const [portOfDischarge, setPortOfDischarge] = useState<Port | null>(null);
  const [portFrom, setPortFrom] = useState<Port | null>(null);
  const [portTo, setPortTo] = useState<Port | null>(null);
  const [userType, setUserType] = useState<UserType>(UserType.Consignee);
  const [shippingType, setShippingType] = useState<string | null>(null);
  const [incoterm, setIncoterm] = useState<Incoterms | null>(null);
  const [incotermLocations, setIncotermLocations] = useState<Port[]>([]);
  const [selectedIncotermLocation, setSelectedIncotermLocation] = useState<Port | null>(null);
  const [selectedHSCodes, setSelectedHSCodes] = useState<HSCodes[]>([]);
  const [addCargoData, setAddCargoData] = useState<AddCargoData[][]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedStep, setSelectedStep] = useState<number>(0);
  const [fromData, setFromData] = useState<RequestCollectionRegistration | null>(null);
  const [toData, setToData] = useState<RequestCollectionRegistration | null>(null);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
  const [billOfLading, setBillOfLading] = useState<BillOfLading | null>(null);
  const [shipperError, setShipperError] = useState<string | null>(null);
  const [consigneeError, setConsigneeError] = useState<string | null>(null);
  const [sellerError, setSellerError] = useState<string | null>(null);
  const [buyerError, setBuyerError] = useState<string | null>(null);
  const [podError, setPodError] = useState<string | null>(null);
  const [polError, setPolError] = useState<string | null>(null);
  const [collectionDetailsError, setCollectionDetailsError] = useState<string | null>(null);
  const [deliveryDetailsError, setDeliveryDetailsError] = useState<string | null>(null);
  const [incotermError, setIncotermError] = useState<string | null>(null);
  const [incotermLocationError, setIncotermLocationError] = useState<string | null>(null);
  const [billOfLadingError, setBillOfLadingError] = useState<string | null>(null);
  const [addCargoError, setAddCargoError] = useState<string | null>(null);
  const [hsCodesError, setHsCodesError] = useState<string | null>(null);
  const [invalidDocuments, setInvalidDocuments] = useState<boolean>(false);
  const [canCollectShipment, setCanCollectShipment] = useState<boolean>(true);
  const [canSetDeliverToPort, setCanSetDeliverToPort] = useState<boolean>(false);
  const [canDeliverShipment, setCanDeliverShipment] = useState<boolean>(true);
  const [canSetPickUpPort, setCanSetPickUpPort] = useState<boolean>(false);
  const [fallbackPort, setFallbackPort] = useState<Port | null>(null);
  const [loadingCreateBooking, setLoadingCreateBooking] = useState<boolean>(false);
  const [loadingCopyBooking, setLoadingCopyBooking] = useState<boolean>(true);
  const acceptedFileTypes: string[] = [
    AcceptedFileType.PDF,
    AcceptedFileType.JPEG,
    AcceptedFileType.XLSX,
    AcceptedFileType.DOC,
    AcceptedFileType.DOCX,
    AcceptedFileType.XLS,
  ];
  const [partnerAddress, setPartnerAddress] = useState<CompanyAddress | null>(null);
  const [isSameAsShipper, setIsSameAsShipper] = useState(false);
  const [isSameAsConsignee, setIsSameAsConsignee] = useState(false);
  const [isICS2Required, setIsICS2Required] = useState(false);

  const [filteredIncoterms, setFilteredIncoterms] = useState<Incoterms[]>([]);

  const handlePortOfLoadOnShipperDataChange = (shipper: PickPartnerChangeRegistration | null) => {
    if (shipper?.placeCity?.CountryCode === "IS") {
      if (props.freightMode === FreightMode.SH) {
        const port = ArrayUtilities.getObject<Port>(portsAll, "ISREY", "PointCode");
        setPortOfLoad(port || null);
      }
      if (props.freightMode === FreightMode.AI) {
        CreateBookingService.searchPorts({
          portType: PortType.AIR,
          country: "IS",
          searchString: "REK",
        }).then((p) => setPortOfLoad(p[0] || null));
      }
    } else {
      if (props.pageType === PageType.COPY) {
        setPortsOfLoad(portsAll);
        setPortOfLoad(portOfLoad);
      } else {
        setPortOfLoad(null);
      }
    }
  };

  const handlePortOfDischargeOnConsigneeDataChange = (
    consignee: PickPartnerChangeRegistration | null
  ) => {
    if (consignee?.placeCity?.CountryCode === "IS") {
      if (props.freightMode === FreightMode.SH) {
        const port = ArrayUtilities.getObject<Port>(portsAll, "ISREY", "PointCode");
        setPortOfDischarge(port || null);
      }
      if (props.freightMode === FreightMode.AI) {
        CreateBookingService.searchPorts({
          portType: PortType.AIR,
          country: "IS",
          searchString: "REK",
        }).then((p) => {
          setPortOfDischarge(p[0] || null);
        });
      }
    } else {
      if (props.pageType === PageType.COPY) {
        setPortsOfDischarge(portsAll);
        setPortOfDischarge(portOfDischarge);
      } else {
        setPortOfDischarge(null);
      }
    }
  };

  const setDataByUserType = (partnerAddress: CompanyAddress | null) => {
    if (userType === UserType.Shipper) {
      const shipper = {
        companyName: userProfile?.SelectedCompany?.FullName ?? "",
        address: userProfile?.SelectedCompany?.Address2 ?? "",
        addressCode: partnerAddress?.AddressCode ?? "",
        postalCode: userProfile?.SelectedCompany?.PostCode ?? "",
        placeCity: {
          PointCode: partnerAddress?.PointCode ?? "",
          FullName: partnerAddress?.City ?? "",
          Country: partnerAddress?.CountryName ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          PortCode: partnerAddress?.PostCode ?? "",
          LocalName: "",
          PortType: props.freightMode === FreightMode.AI ? PortType.AIR : PortType.SEA,
          TimeZone: "",
          TimeZoneDiff: 0,
        },
        contact: userProfile?.User?.Username ?? "",
        email: userProfile?.Access?.Email ?? "",
        phone: userProfile?.User?.Phone ?? "",
        partner: {
          PartnerCode: partnerAddress?.PartnerCode ?? "",
          FullName: partnerAddress?.FullName ?? "",
          PartnerSubCode: partnerAddress?.PartnerSubCode ?? "",
          CompanyCode: partnerAddress?.CompanyCode ?? "",
          AddressType: partnerAddress?.AddressType ?? "",
          AddressCode: partnerAddress?.AddressCode ?? "",
          PointCode: partnerAddress?.PointCode ?? "",
          PostCode: partnerAddress?.PostCode ?? "",
          Address1: partnerAddress?.Address1 ?? "",
          Address2: partnerAddress?.Address2 ?? "",
          Address3: partnerAddress?.Address3 ?? "",
          Address4: partnerAddress?.Address4 ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          StateCode: partnerAddress?.StateCode ?? "",
          CountryName: partnerAddress?.CountryName ?? "",
          AddressTypeDescription: partnerAddress?.AddressTypeDescription ?? "",
          PointName: partnerAddress?.PointName ?? "",
          ContactNumber: userProfile?.User?.Phone ?? "",
          ContactName: userProfile?.User?.Username ?? "",
          ContactEmail: userProfile?.User?.SendEmail ?? "",
          City: partnerAddress?.City ?? "",
          Currency: "",
          EORINumber: partnerAddress?.EORINumber ?? "",
          VATNumber: partnerAddress?.VATNumber ?? "",
        },
      };
      setShipperData(shipper);
      handlePortOfLoadOnShipperDataChange(shipper);
    }
    const consignee = {
      companyName: userProfile?.SelectedCompany?.FullName ?? "",
      address: userProfile?.SelectedCompany?.Address2 ?? "",
      addressCode: partnerAddress?.AddressCode ?? "",
      postalCode: userProfile?.SelectedCompany?.PostCode ?? "",
      placeCity: {
        PointCode: partnerAddress?.PointCode ?? "",
        FullName: partnerAddress?.City ?? "",
        Country: partnerAddress?.CountryName ?? "",
        CountryCode: partnerAddress?.CountryCode ?? "",
        PortCode: partnerAddress?.PostCode ?? "",
        LocalName: "",
        PortType: props.freightMode === FreightMode.AI ? PortType.AIR : PortType.SEA,
        TimeZone: "",
        TimeZoneDiff: 0,
      },
      contact: userProfile?.User?.Username ?? "",
      email: userProfile?.Access?.Email ?? "",
      phone: userProfile?.User?.Phone ?? "",
      partner: {
        PartnerCode: partnerAddress?.PartnerCode ?? "",
        FullName: partnerAddress?.FullName ?? "",
        PartnerSubCode: partnerAddress?.PartnerSubCode ?? "",
        CompanyCode: partnerAddress?.CompanyCode ?? "",
        AddressType: partnerAddress?.AddressType ?? "",
        AddressCode: partnerAddress?.AddressCode ?? "",
        PointCode: partnerAddress?.PointCode ?? "",
        PostCode: partnerAddress?.PostCode ?? "",
        Address1: partnerAddress?.Address1 ?? "",
        Address2: partnerAddress?.Address2 ?? "",
        Address3: partnerAddress?.Address3 ?? "",
        Address4: partnerAddress?.Address4 ?? "",
        CountryCode: partnerAddress?.CountryCode ?? "",
        StateCode: partnerAddress?.StateCode ?? "",
        CountryName: partnerAddress?.CountryName ?? "",
        AddressTypeDescription: partnerAddress?.AddressTypeDescription ?? "",
        PointName: partnerAddress?.PointName ?? "",
        ContactNumber: userProfile?.User?.Phone ?? "",
        ContactName: userProfile?.User?.Username ?? "",
        ContactEmail: userProfile?.User?.SendEmail ?? "",
        City: partnerAddress?.City ?? "",
        Currency: "",
        EORINumber: partnerAddress?.EORINumber ?? "",
        VATNumber: partnerAddress?.VATNumber ?? "",
      },
    };
    setConsigneeData(consignee);
    consignee.placeCity.CountryCode === "IS" ? setIsICS2Required(false) : setIsICS2Required(true);
    handlePortOfDischargeOnConsigneeDataChange(consignee);
  };

  const resetState = () => {
    if (userType === UserType.Shipper) {
      setConsigneeData(null);
      setIsICS2Required(false);
    } else setShipperData(null);
    setPortOfLoad(null);
    setPortOfDischarge(null);
    setPortFrom(null);
    setPortTo(null);
    setPortsOfLoad(null);
    setPortsOfDischarge(null);
    setUserType(UserType.Consignee);
    setShippingType(null);
    setIncoterm(null);
    setIncotermLocations([]);
    setSelectedHSCodes([]);
    setAddCargoData([]);
    setDocuments([]);
    setSelectedStep(0);
    setNotifierData(null);
    setSellerData(null);
    setBuyerData(null);
    setFromData(null);
    setToData(null);
    setDataByUserType(null);
    setPodError(null);
    setPolError(null);
    setShipperError(null);
    setConsigneeError(null);
    setSellerError(null);
    setBuyerError(null);
    setCollectionDetailsError(null);
    setDeliveryDetailsError(null);
    setIncotermError(null);
    setIncotermLocationError(null);
    setInvalidDocuments(false);
    setQuoteNumber(null);
    setBillOfLading(null);
    setBillOfLadingError(null);
    setLoadingCreateBooking(false);
    setIsSameAsShipper(false);
    setIsSameAsConsignee(false);
  };

  const initializeBooking = async (
    routeBooking: RouteBookingResponse,
    cargoBooking: CargoBookingResponse,
    ports: Port[]
  ) => {
    const consigneePlace = routeBooking.Consignee?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.Consignee?.PointCode,
          props.freightMode
        )
      : null;
    const shipperPlace = routeBooking.Shipper?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.Shipper?.PointCode,
          props.freightMode
        )
      : null;
    const notifierPlace = routeBooking.Notifier?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.Notifier?.PointCode,
          props.freightMode
        )
      : null;
    const sellerPlace = routeBooking.Seller?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.Seller?.PointCode,
          props.freightMode
        )
      : null;
    const buyerPlace = routeBooking.Buyer?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.Buyer?.PointCode,
          props.freightMode
        )
      : null;
    const colPlace = routeBooking.CollectionAddress?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.CollectionAddress?.PointCode,
          props.freightMode
        )
      : null;
    const delPlace = routeBooking.DeliveryAddress?.PointCode
      ? await CreateBookingService.getPortByPointCode(
          routeBooking.DeliveryAddress?.PointCode,
          props.freightMode
        )
      : null;
    const consignee: PickPartnerChangeRegistration = {
      partner: {
        CompanyCode: routeBooking?.Consignee?.FullName ?? "",
        PartnerCode: routeBooking?.Consignee?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.Consignee?.AddressType ?? "",
        AddressCode: routeBooking?.Consignee?.AddressCode ?? "",
        FullName: routeBooking?.Consignee?.FullName ?? "",
        PointCode: routeBooking?.Consignee?.PointCode ?? "",
        PostCode: routeBooking?.Consignee?.PostCode ?? "",
        Address1: routeBooking?.Consignee?.Address1 ?? "",
        Address2: routeBooking?.Consignee?.Address2 ?? "",
        Address3: routeBooking?.Consignee?.Address3 ?? "",
        Address4: routeBooking?.Consignee?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.Consignee?.ContactNumber ?? "",
        ContactName: routeBooking?.Consignee?.ContactName ?? "",
        ContactEmail: routeBooking?.Consignee?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.Consignee?.Currency ?? "",
        EORINumber: routeBooking?.Consignee?.EORINumber ?? "",
        VATNumber: routeBooking?.Consignee?.VATNumber ?? "",
      },
      placeCity: consigneePlace,
      companyName: routeBooking?.Consignee?.FullName ?? "",
      address: routeBooking?.Consignee?.Address2
        ? routeBooking?.Consignee?.Address2
        : routeBooking?.Consignee?.Address3 ?? "",
      addressCode: routeBooking?.Consignee?.AddressCode ?? "",
      postalCode: routeBooking?.Consignee?.PostCode ?? "",
      contact: routeBooking?.Consignee?.ContactName ?? "",
      email: routeBooking?.Consignee?.ContactEmail ?? "",
      phone: routeBooking?.Consignee?.ContactNumber ?? "",
      eoriNumber: routeBooking?.Consignee?.EORINumber ?? "",
      vatNumber: routeBooking?.Consignee?.VATNumber ?? "",
    };
    const shipper: PickPartnerChangeRegistration = {
      partner: {
        CompanyCode: routeBooking?.Shipper?.FullName ?? "",
        PartnerCode: routeBooking?.Shipper?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.Shipper?.AddressType ?? "",
        AddressCode: routeBooking?.Shipper?.AddressCode ?? "",
        FullName: routeBooking?.Shipper?.FullName ?? "",
        PointCode: routeBooking?.Shipper?.PointCode ?? "",
        PostCode: routeBooking?.Shipper?.PostCode ?? "",
        Address1: routeBooking?.Shipper?.Address1 ?? "",
        Address2: routeBooking?.Shipper?.Address2 ?? "",
        Address3: routeBooking?.Shipper?.Address3 ?? "",
        Address4: routeBooking?.Shipper?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.Shipper?.ContactNumber ?? "",
        ContactName: routeBooking?.Shipper?.ContactName ?? "",
        ContactEmail: routeBooking?.Shipper?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.Shipper?.Currency ?? "",
        EORINumber: routeBooking?.Shipper?.EORINumber ?? "",
        VATNumber: routeBooking?.Shipper?.VATNumber ?? "",
      },
      placeCity: shipperPlace,
      companyName: routeBooking?.Shipper?.FullName ?? "",
      address: routeBooking?.Shipper?.Address2
        ? routeBooking?.Shipper?.Address2
        : routeBooking?.Shipper?.Address3 ?? "",
      addressCode: routeBooking?.Shipper?.AddressCode ?? "",
      postalCode: routeBooking?.Shipper?.PostCode ?? "",
      contact: routeBooking?.Shipper?.ContactName ?? "",
      email: routeBooking?.Shipper?.ContactEmail ?? "",
      phone: routeBooking?.Shipper?.ContactNumber ?? "",
      eoriNumber: routeBooking?.Shipper?.EORINumber ?? "",
      vatNumber: routeBooking?.Shipper?.VATNumber ?? "",
    };
    const notifier: PickPartnerChangeRegistration = {
      partner: {
        CompanyCode: routeBooking?.Notifier?.FullName ?? "",
        PartnerCode: routeBooking?.Notifier?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.Notifier?.AddressType ?? "",
        AddressCode: routeBooking?.Notifier?.AddressCode ?? "",
        FullName: routeBooking?.Notifier?.FullName ?? "",
        PointCode: routeBooking?.Notifier?.PointCode ?? "",
        PostCode: routeBooking?.Notifier?.PostCode ?? "",
        Address1: routeBooking?.Notifier?.Address1 ?? "",
        Address2: routeBooking?.Notifier?.Address2 ?? "",
        Address3: routeBooking?.Notifier?.Address3 ?? "",
        Address4: routeBooking?.Notifier?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.Notifier?.ContactNumber ?? "",
        ContactName: routeBooking?.Notifier?.ContactName ?? "",
        ContactEmail: routeBooking?.Notifier?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.Notifier?.Currency ?? "",
        EORINumber: routeBooking?.Notifier?.EORINumber ?? "",
        VATNumber: routeBooking?.Notifier?.VATNumber ?? "",
      },
      placeCity: notifierPlace,
      companyName: routeBooking?.Notifier?.FullName ?? "",
      address: routeBooking?.Notifier?.Address2
        ? routeBooking?.Notifier?.Address2
        : routeBooking?.Notifier?.Address3 ?? "",
      addressCode: routeBooking?.Notifier?.AddressCode ?? "",
      postalCode: routeBooking?.Notifier?.PostCode ?? "",
      contact: routeBooking?.Notifier?.ContactName ?? "",
      email: routeBooking?.Notifier?.ContactEmail ?? "",
      phone: routeBooking?.Notifier?.ContactNumber ?? "",
      eoriNumber: routeBooking?.Notifier?.EORINumber ?? "",
      vatNumber: routeBooking?.Notifier?.VATNumber ?? "",
    };
    const seller: PickPartnerChangeRegistration = {
      partner: {
        CompanyCode: routeBooking?.Seller?.FullName ?? "",
        PartnerCode: routeBooking?.Seller?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.Seller?.AddressType ?? "",
        AddressCode: routeBooking?.Seller?.AddressCode ?? "",
        FullName: routeBooking?.Seller?.FullName ?? "",
        PointCode: routeBooking?.Seller?.PointCode ?? "",
        PostCode: routeBooking?.Seller?.PostCode ?? "",
        Address1: routeBooking?.Seller?.Address1 ?? "",
        Address2: routeBooking?.Seller?.Address2 ?? "",
        Address3: routeBooking?.Seller?.Address3 ?? "",
        Address4: routeBooking?.Seller?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.Seller?.ContactNumber ?? "",
        ContactName: routeBooking?.Seller?.ContactName ?? "",
        ContactEmail: routeBooking?.Seller?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.Seller?.Currency ?? "",
        EORINumber: routeBooking?.Seller?.EORINumber ?? "",
        VATNumber: routeBooking?.Seller?.VATNumber ?? "",
      },
      placeCity: sellerPlace,
      companyName: routeBooking?.Seller?.FullName ?? "",
      address: routeBooking?.Seller?.Address2
        ? routeBooking?.Seller?.Address2
        : routeBooking?.Seller?.Address3 ?? "",
      addressCode: routeBooking?.Seller?.AddressCode ?? "",
      postalCode: routeBooking?.Seller?.PostCode ?? "",
      contact: routeBooking?.Seller?.ContactName ?? "",
      email: routeBooking?.Seller?.ContactEmail ?? "",
      phone: routeBooking?.Seller?.ContactNumber ?? "",
      eoriNumber: routeBooking?.Seller?.EORINumber ?? "",
      vatNumber: routeBooking?.Seller?.VATNumber ?? "",
    };
    const buyer: PickPartnerChangeRegistration = {
      partner: {
        CompanyCode: routeBooking?.Buyer?.FullName ?? "",
        PartnerCode: routeBooking?.Buyer?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.Buyer?.AddressType ?? "",
        AddressCode: routeBooking?.Buyer?.AddressCode ?? "",
        FullName: routeBooking?.Buyer?.FullName ?? "",
        PointCode: routeBooking?.Buyer?.PointCode ?? "",
        PostCode: routeBooking?.Buyer?.PostCode ?? "",
        Address1: routeBooking?.Buyer?.Address1 ?? "",
        Address2: routeBooking?.Buyer?.Address2 ?? "",
        Address3: routeBooking?.Buyer?.Address3 ?? "",
        Address4: routeBooking?.Buyer?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.Buyer?.ContactNumber ?? "",
        ContactName: routeBooking?.Buyer?.ContactName ?? "",
        ContactEmail: routeBooking?.Buyer?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.Buyer?.Currency ?? "",
        EORINumber: routeBooking?.Buyer?.EORINumber ?? "",
        VATNumber: routeBooking?.Buyer?.VATNumber ?? "",
      },
      placeCity: buyerPlace,
      companyName: routeBooking?.Buyer?.FullName ?? "",
      address: routeBooking?.Buyer?.Address2
        ? routeBooking?.Buyer?.Address2
        : routeBooking?.Buyer?.Address3 ?? "",
      addressCode: routeBooking?.Buyer?.AddressCode ?? "",
      postalCode: routeBooking?.Buyer?.PostCode ?? "",
      contact: routeBooking?.Buyer?.ContactName ?? "",
      email: routeBooking?.Buyer?.ContactEmail ?? "",
      phone: routeBooking?.Buyer?.ContactNumber ?? "",
      eoriNumber: routeBooking?.Buyer?.EORINumber ?? "",
      vatNumber: routeBooking?.Buyer?.VATNumber ?? "",
    };
    const collectionAddress: RequestCollectionRegistration = {
      partner: {
        CompanyCode: routeBooking?.CollectionAddress?.FullName ?? "",
        PartnerCode: routeBooking?.CollectionAddress?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.CollectionAddress?.AddressType ?? "",
        AddressCode: routeBooking?.CollectionAddress?.AddressCode ?? "",
        FullName: routeBooking?.CollectionAddress?.FullName ?? "",
        PointCode: routeBooking?.CollectionAddress?.PointCode ?? "",
        PostCode: routeBooking?.CollectionAddress?.PostCode ?? "",
        Address1: routeBooking?.CollectionAddress?.Address1 ?? "",
        Address2: routeBooking?.CollectionAddress?.Address2 ?? "",
        Address3: routeBooking?.CollectionAddress?.Address3 ?? "",
        Address4: routeBooking?.CollectionAddress?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.CollectionAddress?.ContactNumber ?? "",
        ContactName: routeBooking?.CollectionAddress?.ContactName ?? "",
        ContactEmail: routeBooking?.CollectionAddress?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.CollectionAddress?.Currency ?? "",
        EORINumber: "",
        VATNumber: "",
      },
      placeCity: colPlace,
      companyName: routeBooking?.CollectionAddress?.FullName ?? "",
      address: routeBooking?.CollectionAddress?.Address2
        ? routeBooking?.CollectionAddress?.Address2
        : routeBooking?.CollectionAddress?.Address3 ?? "",
      addressCode: routeBooking?.CollectionAddress?.AddressCode ?? "",
      postalCode: routeBooking?.CollectionAddress?.PostCode ?? "",
      contact: routeBooking?.CollectionAddress?.ContactName ?? "",
      email: routeBooking?.CollectionAddress?.ContactEmail ?? "",
      phone: routeBooking?.CollectionAddress?.ContactNumber ?? "",
      collectionDate: null,
      from: "",
      to: "",
      collectionReference: "",
      additionalInfo: routeBooking?.CollectionAddress?.SpecialInstructions ?? "",
    };
    const deliveryAddress: RequestCollectionRegistration = {
      partner: {
        CompanyCode: routeBooking?.DeliveryAddress?.FullName ?? "",
        PartnerCode: routeBooking?.DeliveryAddress?.PartnerCode ?? "",
        PartnerSubCode: "",
        AddressType: routeBooking?.DeliveryAddress?.AddressType ?? "",
        AddressCode: routeBooking?.DeliveryAddress?.AddressCode ?? "",
        FullName: routeBooking?.DeliveryAddress?.FullName ?? "",
        PointCode: routeBooking?.DeliveryAddress?.PointCode ?? "",
        PostCode: routeBooking?.DeliveryAddress?.PostCode ?? "",
        Address1: routeBooking?.DeliveryAddress?.Address1 ?? "",
        Address2: routeBooking?.DeliveryAddress?.Address2 ?? "",
        Address3: routeBooking?.DeliveryAddress?.Address3 ?? "",
        Address4: routeBooking?.DeliveryAddress?.Address4 ?? "",
        CountryCode: "",
        StateCode: "",
        CountryName: "",
        AddressTypeDescription: "",
        PointName: "",
        ContactNumber: routeBooking?.DeliveryAddress?.ContactNumber ?? "",
        ContactName: routeBooking?.DeliveryAddress?.ContactName ?? "",
        ContactEmail: routeBooking?.DeliveryAddress?.ContactEmail ?? "",
        City: "",
        Currency: routeBooking?.DeliveryAddress?.Currency ?? "",
        EORINumber: "",
        VATNumber: "",
      },
      placeCity: delPlace,
      companyName: routeBooking?.DeliveryAddress?.FullName ?? "",
      address: routeBooking?.DeliveryAddress?.Address2
        ? routeBooking?.DeliveryAddress?.Address2
        : routeBooking?.DeliveryAddress?.Address3 ?? "",
      addressCode: routeBooking?.DeliveryAddress?.AddressCode ?? "",
      postalCode: routeBooking?.DeliveryAddress?.PostCode ?? "",
      contact: routeBooking?.DeliveryAddress?.ContactName ?? "",
      email: routeBooking?.DeliveryAddress?.ContactEmail ?? "",
      phone: routeBooking?.DeliveryAddress?.ContactNumber ?? "",
      collectionDate: null,
      from: "",
      to: "",
      collectionReference: "",
      additionalInfo: routeBooking?.DeliveryAddress?.SpecialInstructions ?? "",
    };
    const pol: Port = {
      PointCode: routeBooking?.POL?.PointCode ?? "",
      FullName: routeBooking?.POL?.FullName ?? "",
      Country: routeBooking?.POL?.Country ?? "",
      CountryCode: routeBooking?.POL?.CountryCode ?? "",
      PortCode: routeBooking?.POL?.PortCode ?? "",
      LocalName: routeBooking?.POL?.LocalName ?? "",
      PortType: routeBooking?.POL?.PortType ?? "",
      TimeZone: routeBooking?.POL?.TimeZone ?? "",
      TimeZoneDiff: routeBooking?.POL?.TimeZoneDiff ?? 0,
    };
    const pod: Port = {
      PointCode: routeBooking?.POD?.PointCode ?? "",
      FullName: routeBooking?.POD?.FullName ?? "",
      Country: routeBooking?.POD?.Country ?? "",
      CountryCode: routeBooking?.POD?.CountryCode ?? "",
      PortCode: routeBooking?.POD?.PortCode ?? "",
      LocalName: routeBooking?.POD?.LocalName ?? "",
      PortType: routeBooking?.POD?.PortType ?? "",
      TimeZone: routeBooking?.POD?.TimeZone ?? "",
      TimeZoneDiff: routeBooking?.POD?.TimeZoneDiff ?? 0,
    };
    const plr: Port = {
      PointCode: routeBooking?.PLR?.PointCode ?? "",
      FullName: routeBooking?.PLR?.FullName ?? "",
      Country: routeBooking?.PLR?.Country ?? "",
      CountryCode: routeBooking?.PLR?.CountryCode ?? "",
      PortCode: routeBooking?.PLR?.PortCode ?? "",
      LocalName: routeBooking?.PLR?.LocalName ?? "",
      PortType: routeBooking?.PLR?.PortType ?? "",
      TimeZone: routeBooking?.PLR?.TimeZone ?? "",
      TimeZoneDiff: routeBooking?.PLR?.TimeZoneDiff ?? 0,
    };
    const pfd: Port = {
      PointCode: routeBooking?.PFD?.PointCode ?? "",
      FullName: routeBooking?.PFD?.FullName ?? "",
      Country: routeBooking?.PFD?.Country ?? "",
      CountryCode: routeBooking?.PFD?.CountryCode ?? "",
      PortCode: routeBooking?.PFD?.PortCode ?? "",
      LocalName: routeBooking?.PFD?.LocalName ?? "",
      PortType: routeBooking?.PFD?.PortType ?? "",
      TimeZone: routeBooking?.PFD?.TimeZone ?? "",
      TimeZoneDiff: routeBooking?.PFD?.TimeZoneDiff ?? 0,
    };
    const incoterm = routeBooking?.IncoTerms
      ? await CreateBookingService.getIncotermByCode(routeBooking?.IncoTerms ?? "")
      : null;

    const hsCodes: HSCodes[] = [];
    if (cargoBooking?.HsCodes !== undefined && cargoBooking?.HsCodes !== null) {
      cargoBooking?.HsCodes?.forEach((hs) => {
        hsCodes.push({
          HSCode: hs.HSCode ?? "",
          Description: hs.Description ?? "",
        });
      });
    }

    const cargoDataLCL: AddCargoData[] = [];
    const cargoDataFCL: AddCargoData[][] = [];

    cargoBooking?.Containers?.forEach((container) => {
      container?.ContainerData?.forEach((data) => {
        data?.Details?.forEach(async (detail, index) => {
          const commodity = detail.CommodityCode
            ? await CreateBookingService.getCommodityByCode(detail.CommodityCode)
            : null;
          const containerType = data?.ContainerType
            ? await CreateBookingService.getContainerTypeByCode(data?.ContainerType)
            : null;
          const unit = detail.PackageCode
            ? await CreateBookingService.getPackageTypeByCode(detail.PackageCode)
            : null;

          const marks: string[] = [];
          cargoBooking?.MarksAndNumbers?.forEach((mark) => {
            marks.push(mark.Description ?? "");
          });

          const hazards = props.jobReference
            ? await CreateBookingService.getHazardCodesByJobReference(props.jobReference, index + 1)
            : null;

          if (props.freightMode === FreightMode.SH && routeBooking?.ShippingType === "F") {
            cargoDataFCL.push([
              {
                commodity: commodity,
                quantity: 0,
                containerType: containerType,
                containerShipperOwned: false,
                containerNumber: "",
                containerSealNumber: "",
                unit: unit,
                description: detail?.Description ?? "",
                dimensions: [],
                length: 0,
                width: 0,
                height: 0,
                volume: 0,
                weight: 0,
                temperatureControl: detail?.IsRefrigerated ?? false,
                temperatureMin: detail?.MinTemperature?.toString() ?? "",
                temperatureMax: detail?.MaxTemperature?.toString() ?? "",
                stackable: detail?.IsStackable ?? false,
                hazardous: detail?.IsHazardous ?? false,
                unNumbers: hazards,
                outOfGauge: false,
                marksAndNumbers: cargoBooking?.MarksAndNumbers ? marks?.join(";") : "",
              },
            ]);
          } else {
            cargoDataLCL.push({
              commodity: commodity,
              quantity: 0,
              containerType: containerType,
              containerShipperOwned: false,
              containerNumber: container?.ContainerNumber ?? "",
              containerSealNumber: data?.SealNumber ?? "",
              unit: unit,
              description: detail?.Description ?? "",
              dimensions: [
                {
                  height: 0,
                  width: 0,
                  length: 0,
                  quantity: 0,
                },
              ],
              length: 0,
              width: 0,
              height: 0,
              volume: 0,
              weight: 0,
              temperatureControl: detail?.IsRefrigerated ?? false,
              temperatureMin: detail?.MinTemperature?.toString() ?? "",
              temperatureMax: detail?.MaxTemperature?.toString() ?? "",
              stackable: detail?.IsStackable ?? false,
              hazardous: detail?.IsHazardous ?? false,
              unNumbers: hazards,
              outOfGauge: false,
              marksAndNumbers: cargoBooking?.MarksAndNumbers ? marks?.join(";") : "",
            });
          }
        });
      });
    });

    const incotermLocation = ArrayUtilities.getObject<Port>(
      ports,
      routeBooking?.IncoTermsPoint ?? "",
      "FullName"
    );

    setShipperData(shipper);
    setConsigneeData(consignee);
    consignee?.placeCity?.CountryCode === "IS" ? setIsICS2Required(false) : setIsICS2Required(true);
    setNotifierData(routeBooking?.Notifier ? notifier : null);
    setSellerData(routeBooking?.Seller ? seller : null);
    setBuyerData(routeBooking?.Buyer ? buyer : null);
    setIsSameAsShipper(false);
    setIsSameAsConsignee(false);
    setPortOfLoad(pol);
    setPortOfDischarge(pod);
    setPortFrom(plr);
    setPortTo(pfd);

    if (userProfile?.SelectedCompany.PartnerCode === shipper.partner?.PartnerCode) {
      setUserType(UserType.Shipper);
    } else if (userProfile?.SelectedCompany.PartnerCode === consignee.partner?.PartnerCode) {
      setUserType(UserType.Consignee);
    }

    setShippingType(routeBooking?.ShippingType === "L" ? ShippingType.LCL : ShippingType.FCL);
    setIncoterm(incoterm);
    setSelectedIncotermLocation(incotermLocation ? incotermLocation : null);
    setSelectedHSCodes(hsCodes);
    setAddCargoData(
      props.freightMode === FreightMode.SH && routeBooking?.ShippingType === "F"
        ? cargoDataFCL
        : [cargoDataLCL]
    );
    setSelectedStep(0);
    setFromData(collectionAddress);
    setToData(deliveryAddress);
    setQuoteNumber(routeBooking?.QuoteNumber ?? "");
    setBillOfLading(bills.find((x) => x.value === routeBooking?.BOLNumber) ?? null);
    if (routeBooking?.CollectionAddress != null) {
      setCanCollectShipment(true);
      setCanSetDeliverToPort(false);
    } else {
      setCanCollectShipment(false);
      setCanSetDeliverToPort(true);
    }
    if (routeBooking?.DeliveryAddress != null) {
      setCanDeliverShipment(true);
      setCanSetPickUpPort(false);
    } else {
      setCanDeliverShipment(false);
      setCanSetPickUpPort(true);
    }
  };

  const onChangeIncoterm = (option: Incoterms | null) => {
    setIncoterm(option);
  };

  const onChangeIncotermLocation = (incotermLocations: Port[]) => {
    setIncotermLocations(incotermLocations);
  };

  const onChangeSelectedIncotermLocation = (option: Port | null) => {
    setSelectedIncotermLocation(option);
  };

  /**
   * Update the selectable incoterm places based on what incoterm was selected
   * and what inputs were filled in
   */
  const updateIncotermPlaces = (option: Incoterms | null) => {
    const selectedIncoterm = incoterms?.find((x) => x.TermsCode === option?.TermsCode);
    if (selectedIncoterm === undefined) {
      return;
    }

    const incotermPlaces = IncotermUtilities.getIncotermPorts(selectedIncoterm, {
      PLR: fromData?.placeCity ?? null,
      POL: portOfLoad,
      PFD: toData?.placeCity ?? null,
      POD: portOfDischarge,
    });
    onChangeIncotermLocation(incotermPlaces);
    onChangeSelectedIncotermLocation(incotermPlaces[0]);
  };

  /**
   * Filter out the incoterms that depend only on PLR/PFD
   * but they are not filled out in the form
   * @returns Filtered array of incoterms that can be selected
   */
  const filterIncoterms = () => {
    let filteredIncoterms = incoterms ? [...incoterms] : [];
    if (userType === UserType.Consignee && canCollectShipment) {
      filteredIncoterms = filteredIncoterms.filter((it: Incoterms) => {
        return ["EXW", "FCA"].some((type) => type === it.TermsCode);
      });
    }
    if (fromData?.placeCity) {
      filteredIncoterms = filteredIncoterms.filter((it: Incoterms) => {
        return !(
          it.AllowedIncotermsPoints?.length === 1 &&
          it.AllowedIncotermsPoints[0] === IncotermPoint.PLR
        );
      });
    }
    if (toData?.placeCity || portOfDischarge) {
      filteredIncoterms = filteredIncoterms.filter((it: Incoterms) => {
        return !(
          it.AllowedIncotermsPoints?.length === 1 &&
          it.AllowedIncotermsPoints[0] === IncotermPoint.PFD
        );
      });
    }
    // Get the "TermsCode" of the incoterms that were filtered out from the original incoterm list
    const removedTermCodes = difference(incoterms || [], filteredIncoterms).map(
      (it: Incoterms) => it.TermsCode
    );

    // If the selected incoterm was one of the values that got filtered out, reset it's value
    if (incoterm != null && removedTermCodes.indexOf(incoterm.TermsCode) > -1) {
      onChangeIncoterm(null);
      updateIncotermPlaces(incoterm);
    }
    return filteredIncoterms;
  };

  useEffect(() => {
    if (props.pageType === PageType.CREATE) {
      resetState();
    }
    setUserType(UserType.Consignee);

    Promise.all([
      dispatch(getUserProfileFromAPI()).unwrap(),
      dispatch(
        getPorts(props.freightMode === FreightMode.AI ? PortType.POPULARAIR : PortType.POPULARSEA)
      ).unwrap(),
    ]).then(([user]) => {
      dispatch(
        getCompanyAddresses({
          partnerCode: user?.SelectedCompany.PartnerCode,
          addressType: AddressType.DEFAULT + "," + AddressType.SHP + "," + AddressType.CEE,
        })
      );
      dispatch(
        getCompanyCustomerAddresses({
          partnerCode: user?.SelectedCompany.PartnerCode,
          addressType: AddressType.DEFAULT,
        })
      );

      dispatch(getIncoterms());
      dispatch(getShippingTypes());

      if (props.pageType === PageType.CREATE && userType === UserType.Shipper) {
        Promise.all([
          dispatch(
            getPartnerAddress({
              partnerCode: user?.SelectedCompany?.PartnerCode ?? "",
              addressType: AddressType.SHP,
            })
          ).unwrap(),
        ]).then(([pa]) => {
          setDataByUserType(pa);
          setPartnerAddress(pa);
        });
      }
      if (props.pageType === PageType.CREATE && userType === UserType.Consignee) {
        Promise.all([
          dispatch(
            getPartnerAddress({
              partnerCode: user?.SelectedCompany?.PartnerCode ?? "",
              addressType: AddressType.CEE,
            })
          ).unwrap(),
        ]).then(([pa]) => {
          setDataByUserType(pa);
          setPartnerAddress(pa);
        });
      }
    });

    if (props.pageType === PageType.COPY && props.jobReference) {
      Promise.all([
        dispatch(getRouteBooking(props.jobReference)).unwrap(),
        dispatch(getBookingCargo(props.jobReference)).unwrap(),
        dispatch(
          getPorts(props.freightMode === FreightMode.AI ? PortType.POPULARAIR : PortType.POPULARSEA)
        ).unwrap(),
      ]).then(([routeBooking, cargoBooking, ports]) => {
        initializeBooking(routeBooking, cargoBooking, ports).then(() => {
          setLoadingCopyBooking(false);
        });
      });
    }

    setFilteredIncoterms(filterIncoterms());
  }, []);

  useEffect(() => {
    if (props.pageType === PageType.CREATE && partnerAddress === null && userProfile !== null)
      Promise.all([
        dispatch(
          getPartnerAddress({
            partnerCode: userProfile?.SelectedCompany?.PartnerCode ?? "",
            addressType: AddressType.DEFAULT,
          })
        ).unwrap(),
      ]).then(([pa]) => {
        setDataByUserType(pa);
        setPartnerAddress(pa);
      });
  }, [partnerAddress, userProfile]);

  useEffect(() => {
    setPortsOfLoad(portsAll);
    setPortsOfDischarge(portsAll);
  }, [portsAll]);

  useEffect(() => {
    let fallback_POD_POL_Port;
    if (props.freightMode === FreightMode.SH) {
      fallback_POD_POL_Port = ArrayUtilities.getObject<Port>(portsAll, "NLRTM", "PointCode");
    } else {
      fallback_POD_POL_Port = ArrayUtilities.getObject<Port>(portsAll, "KEF", "PointCode");
    }

    setFallbackPort(fallback_POD_POL_Port || null);
  }, [portsAll]);

  const onChangePortOfLoad = async (option: Port) => {
    setPortOfLoad(option);

    let ports: Port[] = portsAll;

    if (props.freightMode === FreightMode.AI) {
      ports = await CreateBookingService.searchPorts({
        portType: PortType.POPULARAIR,
        searchString: "",
        country: "IS",
        size: undefined,
      });
      setPortsOfLoad(ports);
      return;
    }

    if (option != null) {
      CreateBookingService.getPortforPool(option?.PointCode, "POL", props.freightMode).then(
        (port: Port) => {
          if (port && props.freightMode === FreightMode.SH) setPortFrom(port);
          else setPortFrom(fallbackPort);
        }
      );
      if (option?.CountryCode === "IS") {
        ports = portsAll.filter((y) => y.CountryCode != "IS");
      } else {
        ports = await CreateBookingService.searchPorts({
          portType: PortType.SEA,
          searchString: "",
          country: "IS",
          size: undefined,
        });
        if (!portOfDischarge) setPortOfDischarge(fallbackPort);
      }
    }

    setPortsOfDischarge(ports);
  };

  const onChangePortOfDischarge = async (option: Port) => {
    setPortOfDischarge(option);

    let ports: Port[] = portsAll;

    if (props.freightMode === FreightMode.AI) {
      ports = ports = portsAll.filter((y) => y.CountryCode != "IS");
      setPortsOfDischarge(ports);
      return;
    }

    if (option != null) {
      CreateBookingService.getPortforPool(option?.PointCode, "POL", props.freightMode).then(
        (port: Port) => {
          if (port && props.freightMode === FreightMode.SH) setPortTo(port);
          else setPortTo(fallbackPort);
        }
      );

      if (option?.CountryCode === "IS") {
        ports = portsAll.filter((y) => y.CountryCode != "IS");
      } else {
        ports = await CreateBookingService.searchPorts({
          portType: PortType.SEA,
          searchString: "",
          country: "IS",
          size: undefined,
        });
      }
    }

    setPortsOfLoad(ports);
  };

  const onChangeShipperData = (shipperData: PickPartnerChangeRegistration) => {
    setShipperData(shipperData);
    handlePortOfLoadOnShipperDataChange(shipperData);
    if (isSameAsShipper) {
      setIsSameAsShipper(false);
      setSellerData(null);
    }
  };

  const onChangeConsigneeData = (consigneeData: PickPartnerChangeRegistration) => {
    setConsigneeData(consigneeData);
    handlePortOfDischargeOnConsigneeDataChange(consigneeData);
    consigneeData.placeCity?.CountryCode === "IS"
      ? setIsICS2Required(false)
      : setIsICS2Required(true);
    if (isSameAsConsignee) {
      setIsSameAsConsignee(false);
      setBuyerData(null);
    }
  };

  const onChangeNotifierData = (notifierData: PickPartnerChangeRegistration) => {
    setNotifierData(notifierData);
  };

  const onChangeSellerData = (sellerData: PickPartnerChangeRegistration) => {
    setSellerData(sellerData);
  };

  const onChangeBuyerData = (buyerData: PickPartnerChangeRegistration) => {
    setBuyerData(buyerData);
  };

  const onChangeShippingType = (shippingType: string) => {
    setShippingType(shippingType);
    setAddCargoData([]);
  };

  const onChangeQuoteNumber = (value: string) => {
    setQuoteNumber(value);
  };

  const onChangeBillOfLading = (value: BillOfLading) => {
    setBillOfLading(value);
  };

  const resetIncoterm = () => {
    onChangeIncoterm(null);
    setFilteredIncoterms(filterIncoterms());
    onChangeIncotermLocation([]);
    onChangeSelectedIncotermLocation(null);
  };

  const onChangeSelectedHSCodes = (hsCodes: HSCodes[]) => {
    setSelectedHSCodes([...hsCodes]);
  };

  const onChangeCargoData = (addCargo: AddCargoData, containerIndex: number | null) => {
    let cargoData = structuredClone(addCargoData);
    if (containerIndex !== null && containerIndex !== undefined) {
      cargoData = [...cargoData, [addCargo]];
    } else {
      if (cargoData[0]) {
        cargoData[0] = [...cargoData[0], addCargo];
      } else {
        cargoData.push([addCargo]);
      }
    }
    setAddCargoData([...cargoData]);
  };

  const onDeleteCargoData = (cargoIndex: number, containerIndex: number | null) => {
    if (containerIndex !== null && containerIndex !== undefined) {
      setAddCargoData([
        ...addCargoData.slice(0, containerIndex),
        ...addCargoData.slice(containerIndex + 1, addCargoData.length),
      ]);
    } else {
      let cargoData = structuredClone(addCargoData[0]);
      cargoData = [
        ...cargoData.slice(0, cargoIndex),
        ...cargoData.slice(cargoIndex + 1, cargoData.length),
      ];
      setAddCargoData([cargoData]);
    }
  };

  const onEditCargoData = (
    cargoIndex: number,
    containerIndex: number | null,
    addCargo: AddCargoData
  ) => {
    const cargoData = structuredClone(addCargoData);
    if (containerIndex !== null && containerIndex !== undefined) {
      cargoData[containerIndex][cargoIndex] = { ...addCargo };
    } else {
      cargoData[0][cargoIndex] = { ...addCargo };
    }
    setAddCargoData(cargoData);
  };

  const onDocumentAdded = (document: Document) => {
    setDocuments((oldDocs) => [...oldDocs, document]);
  };

  const onDocumentDeleted = (index: number) => {
    setDocuments([...documents.slice(0, index), ...documents.slice(index + 1, documents.length)]);
  };

  const onChangeSelectedDocType = (selectedDocType: SelectOption<string>, index: number) => {
    const updatedList: Document[] = [...documents];
    const updatedDoc = {
      document: updatedList[index].document,
      doctype: selectedDocType.value,
    };
    updatedList[index] = updatedDoc;
    setDocuments(updatedList);
  };

  const createBookingRequest = (): BookingRequest => {
    let plr;
    if (fromData?.placeCity != null) {
      plr = fromData.placeCity;
    }
    let pfd;
    if (toData?.placeCity != null) {
      pfd = toData.placeCity;
    }

    let bl;
    if (userType === UserType.Shipper && props.freightMode === FreightMode.SH) {
      bl = billOfLading?.value ?? "";
    } else {
      bl = "G";
    }

    const details: BookingDetail[] = [];

    if (props.freightMode === FreightMode.SH && shippingType?.charAt(0) === "F") {
      addCargoData.forEach((data) => {
        details.push({
          Dimensions: [
            {
              PackageCode: data[0]?.unit?.PackageCode,
              NumUnits: data[0]?.quantity,
              Weight: data[0]?.weight,
              Volume: data[0]?.volume,
              Length: data[0]?.length,
              Height: data[0]?.height,
              Width: data[0]?.width,
              Hazardous: data[0]?.unNumbers,
              Oversize: {
                OverHeight: data[0]?.height ?? null,
                OverLengthAfter: data[0]?.length ?? null,
                OverLengthFore: data[0]?.length ?? null,
                OverWidthLeft: data[0]?.width ?? null,
                OverWidthRight: data[0]?.width ?? null,
              },
            },
          ],
          MinTemperature: parseInt(data[0]?.temperatureMin),
          MaxTemperature: parseInt(data[0]?.temperatureMax),
          Temperature: null,
          ContainerNumber: data[0]?.containerNumber ?? null,
          ContainerSealNumber: data[0]?.containerSealNumber ?? null,
          Description: data[0]?.description,
          CommodityCode: data[0]?.commodity?.CommodityCode,
          Quantity: data[0]?.quantity,
          PackageCode:
            shippingType?.charAt(0) === "F" ? data[0]?.containerType?.PackageCode : undefined,
          Weight: data[0]?.weight,
          WeightUOM: "KGM",
          Volume: data[0]?.volume,
          VolumeUOM: "CBM",
          IsTemperatureControlled: data[0]?.temperatureControl,
          HazardFlag: data[0]?.hazardous,
          Marks: data[0]?.marksAndNumbers,
          MarksDescription: data[0]?.marksAndNumbers,
        } as BookingDetail);
      });
    } else {
      addCargoData.forEach((data) => {
        data.forEach((detail, cargoindex) => {
          details.push({
            Dimensions: data[cargoindex].dimensions.map((dimension) => {
              return {
                PackageCode: data[cargoindex]?.unit?.PackageCode ?? "",
                NumUnits: dimension?.quantity ?? 0,
                Weight: detail.weight ?? 0,
                Length: dimension?.length ?? 0,
                Height: dimension?.height ?? 0,
                Width: dimension?.width ?? 0,
                Hazardous: data[cargoindex]?.unNumbers ?? [],
                Oversize: null,
              } as BookingDimension;
            }),
            MinTemperature: parseInt(detail.temperatureMin),
            MaxTemperature: parseInt(detail.temperatureMax),
            Temperature: null,
            ContainerNumber: detail.containerNumber ?? null,
            ContainerSealNumber: detail.containerSealNumber ?? null,
            Description: detail.description,
            CommodityCode: detail.commodity?.CommodityCode,
            Quantity: detail.quantity,
            PackageCode:
              shippingType?.charAt(0) === "F" ? detail.containerType?.PackageCode : undefined,
            Weight: detail.weight,
            WeightUOM: "KGM",
            Volume: detail.volume,
            VolumeUOM: "CBM",
            IsTemperatureControlled: detail.temperatureControl,
            HazardFlag: detail.hazardous,
            Marks: detail.marksAndNumbers,
            MarksDescription: detail.marksAndNumbers,
          } as BookingDetail);
        });
      });
    }

    const bookingRequest: BookingRequest = {
      JobReference: null,
      ExternalJobReference: null,
      ControlNumber: null,
      DocumentReference: null,
      Shipper: {
        FullName: shipperData?.companyName,
        Address1: shipperData?.companyName,
        AddressCode: shipperData?.addressCode,
        PointCode:
          shipperData?.partner?.PointCode != null
            ? shipperData?.partner?.PointCode
            : shipperData?.placeCity?.PointCode,
        PostCode: shipperData?.postalCode,
        ContactName: shipperData?.contact,
        ContactEmail: shipperData?.email,
        PartnerCode: shipperData?.partner?.PartnerCode,
        AddressType: AddressType.SHP,
        JobReference: "",
        Address2: shipperData?.address,
        Address3: shipperData?.district,
        Address4: shipperData?.placeCity?.FullName,
        Address5: shipperData?.placeCity?.CountryCode,
        ContactNumber: shipperData?.phone,
        RequestDate: undefined,
        RequestTime: undefined,
        RequestTolerance: undefined,
        SpecialInstructions: "",
        TheirRef: "",
        Currency: "",
        SSN: undefined,
        Town: "",
        EORINumber: shipperData?.eoriNumber,
        VATNumber: shipperData?.vatNumber,
      },
      Consignee: {
        FullName: consigneeData?.companyName,
        Address1: consigneeData?.companyName,
        AddressCode: consigneeData?.addressCode,
        PointCode:
          consigneeData?.partner?.PointCode != null
            ? consigneeData?.partner?.PointCode
            : consigneeData?.placeCity?.PointCode,
        PostCode: consigneeData?.postalCode,
        ContactName: consigneeData?.contact,
        ContactEmail: consigneeData?.email,
        PartnerCode: consigneeData?.partner?.PartnerCode,
        AddressType: AddressType.CEE,
        JobReference: "",
        Address2: consigneeData?.address,
        Address3: consigneeData?.district,
        Address4: consigneeData?.placeCity?.FullName,
        Address5: consigneeData?.placeCity?.CountryCode,
        ContactNumber: consigneeData?.phone,
        RequestDate: undefined,
        RequestTime: undefined,
        RequestTolerance: undefined,
        SpecialInstructions: "",
        TheirRef: "",
        Currency: "",
        SSN: undefined,
        Town: "",
        EORINumber: consigneeData?.eoriNumber,
        VATNumber: consigneeData?.vatNumber,
      },
      Notifier: notifierData?.companyName
        ? {
            FullName: notifierData?.companyName,
            Address1: notifierData?.companyName,
            AddressCode: notifierData?.addressCode,
            PointCode:
              notifierData?.partner?.PointCode != null
                ? notifierData?.partner?.PointCode
                : notifierData?.placeCity?.PointCode,
            PostCode: notifierData?.postalCode,
            ContactName: notifierData?.contact,
            ContactEmail: notifierData?.email,
            PartnerCode: notifierData?.partner?.PartnerCode,
            AddressType: AddressType.NOT,
            JobReference: "",
            Address2: notifierData?.address,
            Address3: notifierData?.district,
            Address4: notifierData?.placeCity?.FullName,
            Address5: notifierData?.placeCity?.CountryCode,
            ContactNumber: notifierData?.phone,
            RequestDate: undefined,
            RequestTime: undefined,
            RequestTolerance: undefined,
            SpecialInstructions: "",
            TheirRef: "",
            Currency: "",
            SSN: undefined,
            Town: "",
            EORINumber: notifierData?.eoriNumber,
            VATNumber: notifierData?.vatNumber,
          }
        : null,
      Seller: sellerData?.companyName
        ? {
            FullName: sellerData?.companyName,
            Address1: sellerData?.companyName,
            AddressCode: sellerData?.addressCode,
            PointCode:
              sellerData?.partner?.PointCode != null
                ? sellerData?.partner?.PointCode
                : sellerData?.placeCity?.PointCode,
            PostCode: sellerData?.postalCode,
            ContactName: sellerData?.contact,
            ContactEmail: sellerData?.email,
            PartnerCode: sellerData?.partner?.PartnerCode,
            AddressType: AddressType.SEL,
            JobReference: "",
            Address2: sellerData?.address,
            Address3: sellerData?.district,
            Address4: sellerData?.placeCity?.FullName,
            Address5: sellerData?.placeCity?.CountryCode,
            ContactNumber: sellerData?.phone,
            RequestDate: undefined,
            RequestTime: undefined,
            RequestTolerance: undefined,
            SpecialInstructions: "",
            TheirRef: "",
            Currency: "",
            SSN: undefined,
            Town: "",
            EORINumber: sellerData?.eoriNumber,
            VATNumber: sellerData?.vatNumber,
          }
        : null,
      Buyer: buyerData?.companyName
        ? {
            FullName: buyerData?.companyName,
            Address1: buyerData?.companyName,
            AddressCode: buyerData?.addressCode,
            PointCode:
              buyerData?.partner?.PointCode != null
                ? buyerData?.partner?.PointCode
                : buyerData?.placeCity?.PointCode,
            PostCode: buyerData?.postalCode,
            ContactName: buyerData?.contact,
            ContactEmail: buyerData?.email,
            PartnerCode: buyerData?.partner?.PartnerCode,
            AddressType: AddressType.BYR,
            JobReference: "",
            Address2: buyerData?.address,
            Address3: buyerData?.district,
            Address4: buyerData?.placeCity?.FullName,
            Address5: buyerData?.placeCity?.CountryCode,
            ContactNumber: buyerData?.phone,
            RequestDate: undefined,
            RequestTime: undefined,
            RequestTolerance: undefined,
            SpecialInstructions: "",
            TheirRef: "",
            Currency: "",
            SSN: undefined,
            Town: "",
            EORINumber: buyerData?.eoriNumber,
            VATNumber: buyerData?.vatNumber,
          }
        : null,
      CollectionAddress: fromData?.companyName
        ? {
            FullName: fromData?.companyName,
            Address1: fromData?.companyName,
            AddressCode: fromData?.addressCode,
            PointCode: fromData?.placeCity?.PointCode,
            PostCode: fromData?.postalCode,
            ContactName: fromData?.contact,
            ContactEmail: fromData?.email,
            PartnerCode: fromData?.partner?.PartnerCode,
            AddressType: AddressType.COL,
            JobReference: "",
            Address2: fromData?.address,
            Address3: fromData?.district,
            Address4: fromData?.placeCity?.FullName,
            Address5: fromData?.placeCity?.CountryCode,
            ContactNumber: fromData?.phone,
            RequestDate: fromData?.collectionDate ?? undefined,
            RequestTime: parseInt(fromData?.from.replace(":", "") ?? "") ?? undefined,
            RequestTolerance: parseInt(fromData?.to.replace(":", "") ?? "") ?? undefined,
            SpecialInstructions: fromData?.additionalInfo,
            TheirRef: fromData?.collectionReference,
            Currency: "",
            SSN: undefined,
            Town: "",
            EORINumber: undefined,
            VATNumber: undefined,
          }
        : null,
      DeliveryAddress: toData?.companyName
        ? {
            FullName: toData?.companyName,
            Address1: toData?.companyName,
            AddressCode: toData?.addressCode,
            PointCode: toData?.placeCity?.PointCode,
            PostCode: toData?.postalCode,
            ContactName: toData?.contact,
            ContactEmail: toData?.email,
            PartnerCode: toData?.partner?.PartnerCode,
            AddressType: AddressType.DEL,
            JobReference: "",
            Address2: toData?.address,
            Address3: toData?.district,
            Address4: toData?.placeCity?.FullName,
            Address5: toData?.placeCity?.CountryCode,
            ContactNumber: toData?.phone,
            RequestDate: undefined,
            RequestTime: undefined,
            RequestTolerance: undefined,
            SpecialInstructions: toData?.additionalInfo,
            TheirRef: toData?.collectionReference,
            Currency: "",
            SSN: undefined,
            Town: "",
            EORINumber: undefined,
            VATNumber: undefined,
          }
        : null,
      BookingPartyReference: null,
      BookingParty: userProfile?.SelectedCompany?.PartnerCode ?? null, //  PartnerCode of the current selected Company  => partnerCode of the one that is creating the booking (SHP or CEE)
      BookingPartyType: userType === UserType.Shipper ? "SHP" : "CEE", // CEE | SHP
      PLR: plr ?? null,
      PFD: pfd ?? null,
      POL: portOfLoad
        ? {
            PointCode: portOfLoad ? portOfLoad.PointCode : "",
            FullName: portOfLoad ? portOfLoad.FullName : "",
            Country: portOfLoad ? portOfLoad.Country : "",
            CountryCode: portOfLoad ? portOfLoad.CountryCode : "",
            PortCode: portOfLoad ? portOfLoad.PortCode : "",
            LocalName: portOfLoad ? portOfLoad.LocalName : "",
            PortType: portOfLoad ? portOfLoad.PortType : "",
            TimeZone: portOfLoad ? portOfLoad.TimeZone : "",
            TimeZoneDiff: portOfLoad ? portOfLoad.TimeZoneDiff : 0,
          }
        : null,
      POD: portOfDischarge
        ? {
            PointCode: portOfDischarge ? portOfDischarge.PointCode : "",
            FullName: portOfDischarge ? portOfDischarge.FullName : "",
            Country: portOfDischarge ? portOfDischarge.Country : "",
            CountryCode: portOfDischarge ? portOfDischarge.CountryCode : "",
            PortCode: portOfDischarge ? portOfDischarge.PortCode : "",
            LocalName: portOfDischarge ? portOfDischarge.LocalName : "",
            PortType: portOfDischarge ? portOfDischarge.PortType : "",
            TimeZone: portOfDischarge ? portOfDischarge.TimeZone : "",
            TimeZoneDiff: portOfDischarge ? portOfDischarge.TimeZoneDiff : 0,
          }
        : null,
      IncoTerms: incoterm ? incoterm?.TermsCode : "",
      IncoTermsPoint: selectedIncotermLocation ? selectedIncotermLocation?.PointCode : "",
      Status: 0,
      VoyageReference: null,
      ShippingType: shippingType?.includes("F") ? ShippingTypeValue.FF : ShippingTypeValue.LL,
      FreightMode: props.freightMode,
      Details: details,
      HsCodes: selectedHSCodes.length > 0 ? selectedHSCodes : null,
      TheirRef: fromData?.collectionReference ?? null,
      QuoteNumber: quoteNumber,
      BOLType: bl,
      Documents: [],
    };

    return bookingRequest;
  };

  const handleUploadDocument = async (
    documentReference: string,
    jobReference: string,
    controlNumber: string,
    userName: string
  ): Promise<UploadDocumentsResponse[]> => {
    if (jobReference != null) {
      return await SingleBookingService.uploadDocuments(
        jobReference,
        documents.map((d) => d.doctype),
        DocumentReferenceType.JOBREFERENCE,
        DocumentReferenceType.USERNAMEREFERENCE,
        userName,
        documents.map((d) => d.document)
      );
    } else if (controlNumber != null) {
      return await SingleBookingService.uploadDocuments(
        controlNumber,
        documents.map((d) => d.doctype),
        DocumentReferenceType.CONTROLNUMBER,
        DocumentReferenceType.USERNAMEREFERENCE,
        userName,
        documents.map((d) => d.document)
      );
    } else {
      return await SingleBookingService.uploadDocuments(
        documentReference,
        documents.map((d) => d.doctype),
        DocumentReferenceType.OTHER,
        DocumentReferenceType.USERNAMEREFERENCE,
        userName,
        documents.map((d) => d.document)
      );
    }
  };

  const handleOnCreateBooking = async () => {
    setLoadingCreateBooking(true);
    const createBooking = createBookingRequest();

    try {
      const response = await CreateBookingService.createBooking(createBooking);

      createBooking.JobReference = response.JobReference;
      createBooking.ControlNumber = response.ControlNumber;
      createBooking.DocumentReference = response.DocumentReference;

      if (createBooking.JobReference != null) {
        navigate(`/createBooking/${props.freightMode}/${createBooking.JobReference}`);
        setLoadingCreateBooking(false);
      } else if (response.IsImportAirFreight) {
        setLoadingCreateBooking(false);
        navigate(`/createBooking/${props.freightMode}/${"importair"}`);
      } else if (response.ErrorMessage && !response.ControlNumber) {
        createBooking.JobReference = "Booking creation failed";
        setLoadingCreateBooking(false);
        navigate(`/createBooking/${props.freightMode}/${"failed"}`);
      } else {
        setLoadingCreateBooking(false);
        navigate(`/createBooking/${props.freightMode}/${"failed"}`);
      }

      if (userProfile?.Access.UserType == "C") {
        let termsNumber;
        if (response.JobReference != null) {
          termsNumber = response.JobReference;
        } else if (response.ControlNumber != null) {
          termsNumber = response.ControlNumber;
        } else {
          termsNumber = response.DocumentReference;
        }
        await CreateBookingService.acceptTerms("BOOKING_CREATE", termsNumber);
      }

      if (documents.length > 0) {
        createBooking.Documents = await handleUploadDocument(
          response.DocumentReference,
          response.JobReference,
          response.ControlNumber,
          userProfile?.User.Username ?? ""
        );
      }

      await CreateBookingService.sendBookingEmail(createBooking);
    } catch (error) {
      await CreateBookingService.sendBookingEmail(createBooking);
      setLoadingCreateBooking(false);
      navigate(`/createBooking/${props.freightMode}/${"failed"}`);
    }
  };

  let fromDataValidation = false;
  let toDataValidation = false;
  if (canCollectShipment && fromData != null) {
    fromDataValidation = true;
  }
  if (canDeliverShipment && toData != null) {
    toDataValidation = true;
  }
  if (canSetDeliverToPort && portOfLoad != null) {
    fromDataValidation = true;
  }
  if (canSetPickUpPort && portOfDischarge != null) {
    toDataValidation = true;
  }
  if (
    canSetDeliverToPort &&
    canSetPickUpPort &&
    portOfLoad != null &&
    portOfDischarge != null &&
    portOfLoad.PointCode == portOfDischarge.PointCode
  ) {
    toDataValidation = false;
    fromDataValidation = false;
  }

  const shipperValidation =
    shipperData != null &&
    shipperData.companyName != "" &&
    shipperData.address != "" &&
    shipperData.postalCode != "" &&
    shipperData.placeCity != null &&
    shipperData.contact != "" &&
    shipperData.email != "";

  const consigneeValidation =
    consigneeData != null &&
    consigneeData.companyName != "" &&
    consigneeData.address != "" &&
    consigneeData.postalCode != "" &&
    consigneeData.placeCity != null &&
    consigneeData.contact != "" &&
    consigneeData.email != "";

  const sellerValidation =
    sellerData != null &&
    sellerData.companyName != "" &&
    sellerData.address != "" &&
    sellerData.postalCode != "" &&
    sellerData.placeCity != null &&
    sellerData.contact != "" &&
    sellerData.email != "";

  const buyerValidation =
    buyerData != null &&
    buyerData.companyName != "" &&
    buyerData.address != "" &&
    buyerData.postalCode != "" &&
    buyerData.placeCity != null &&
    buyerData.contact != "" &&
    buyerData.email != "";

  const isValidRouteComponent = (): boolean => {
    const isShipperRoute = userType === UserType.Shipper && props.freightMode === FreightMode.SH;
    const baseValidation =
      shipperValidation &&
      consigneeValidation &&
      (isICS2Required ? sellerValidation && buyerValidation : true) &&
      fromDataValidation &&
      toDataValidation &&
      incoterm != null &&
      incoterm.TermsCode != "" &&
      selectedIncotermLocation != null;

    return isShipperRoute ? baseValidation && billOfLading != null : baseValidation;
  };

  const onHandleContinueRouteComponent = (): boolean => {
    if (isValidRouteComponent()) {
      setShipperError(null);
      setConsigneeError(null);
      setSellerError(null);
      setBuyerError(null);
      setCollectionDetailsError(null);
      setPodError(null);
      setDeliveryDetailsError(null);
      setPolError(null);
      setIncotermError(null);
      setIncotermLocationError(null);
      setBillOfLadingError(null);
      setSelectedStep(1);
      return true;
    }

    let collectionError = null;
    let deliveryError = null;
    let portOfLoadError = null;
    let portOfDischargeError = null;

    if (!shipperValidation) {
      props.pageType === PageType.CREATE
        ? setShipperError(ErrorTypes.ShipperIsRequired)
        : setShipperError(ErrorTypes.ShipperDetailsRequireChange);
    } else setShipperError(null);

    if (!consigneeValidation) {
      props.pageType === PageType.CREATE
        ? setConsigneeError(ErrorTypes.ConsigneeIsRequired)
        : setConsigneeError(ErrorTypes.ConsigneeDetailsRequireChange);
    } else setConsigneeError(null);

    if (!sellerValidation && isICS2Required) {
      props.pageType === PageType.CREATE
        ? setSellerError(ErrorTypes.SellerIsRequired)
        : setSellerError(ErrorTypes.SellerDetailsRequireChange);
    } else setSellerError(null);

    if (!buyerValidation && isICS2Required) {
      props.pageType === PageType.CREATE
        ? setBuyerError(ErrorTypes.BuyerIsRequired)
        : setBuyerError(ErrorTypes.BuyerDetailsRequireChange);
    } else setBuyerError(null);

    if (canCollectShipment && !fromData?.partner) {
      collectionError = collectionError ?? ErrorTypes.CollectionDetailsIsRequired;
    } else if (canCollectShipment && !fromData?.collectionReference) {
      collectionError = collectionError ?? ErrorTypes.EmptyCollectionDetails;
    } else {
      collectionError = collectionError ?? null;
      portOfDischargeError = portOfDischargeError ?? null;
    }

    if (canSetPickUpPort && !portOfDischarge) {
      portOfDischargeError = portOfDischargeError ?? ErrorTypes.RequiredField;
    } else {
      portOfDischargeError = portOfDischargeError ?? null;
      collectionError = collectionError ?? null;
    }

    if (canDeliverShipment && !toData?.partner) {
      deliveryError = deliveryError ?? ErrorTypes.DeliveryDetailsIsRequired;
    } else {
      deliveryError = deliveryError ?? null;
      portOfLoadError = portOfLoadError ?? null;
    }
    if (canSetDeliverToPort && !portOfLoad) {
      portOfLoadError = portOfLoadError ?? ErrorTypes.RequiredField;
    } else {
      deliveryError = deliveryError ?? null;
      portOfLoadError = portOfLoadError ?? null;
    }

    switch (portOfLoad?.PointCode) {
      case portOfDischarge?.PointCode: {
        portOfLoadError = portOfLoadError ?? ErrorTypes.SamePortNotPossible;
        portOfDischargeError = portOfDischargeError ?? ErrorTypes.SamePortNotPossible;
        break;
      }
      case toData?.placeCity?.PointCode: {
        portOfLoadError = portOfLoadError ?? ErrorTypes.SamePortNotPossible;
        deliveryError = deliveryError ?? ErrorTypes.SamePortNotPossible;
        break;
      }
    }
    switch (fromData?.placeCity?.PointCode) {
      case portOfDischarge?.PointCode: {
        collectionError = collectionError ?? ErrorTypes.SamePortNotPossible;
        portOfDischargeError = portOfDischargeError ?? ErrorTypes.SamePortNotPossible;
        break;
      }
      case toData?.placeCity?.PointCode: {
        collectionError = collectionError ?? ErrorTypes.SamePortNotPossible;
        deliveryError = deliveryError ?? ErrorTypes.SamePortNotPossible;
        break;
      }
    }

    setCollectionDetailsError(collectionError);
    setDeliveryDetailsError(deliveryError);
    setPodError(portOfDischargeError);
    setPolError(portOfLoadError);

    if (fromDataValidation && toDataValidation && incoterm == null) {
      setIncotermError(ErrorTypes.RequiredField);
    }
    if (
      fromDataValidation &&
      toDataValidation &&
      incoterm != null &&
      incoterm.TermsCode != "" &&
      selectedIncotermLocation == null
    ) {
      setIncotermLocationError(ErrorTypes.RequiredField);
    }

    if (
      billOfLading === null &&
      userType === UserType.Shipper &&
      props.freightMode === FreightMode.SH
    ) {
      setBillOfLadingError(ErrorTypes.RequiredField);
    }
    return false;
  };

  const onHandleContinueAddCargoComponent = (): boolean => {
    const invalidData = shippingType?.includes(ShippingType.FCL)
      ? addCargoData === null || addCargoData.length === 0
      : addCargoData[0] === null || addCargoData[0] === undefined || addCargoData[0]?.length === 0;

    let isValid = true;

    if (props.pageType === PageType.CREATE) {
      if (invalidData) {
        setAddCargoError(ErrorTypes.CargoDataIsRequired);
        isValid = false;
      } else {
        setAddCargoError(null);
      }
    }

    if (props.pageType === PageType.COPY) {
      let error = "";
      if (invalidData) {
        setAddCargoError(ErrorTypes.CargoDataIsRequired);
        isValid = false;
      } else {
        addCargoData.forEach((data) => {
          data.forEach((d) => {
            if (
              shippingType?.includes(ShippingType.FCL) &&
              !d.outOfGauge &&
              (!d.quantity || !d.volume || !d.weight)
            ) {
              error = ErrorTypes.EmptyCargoData;
            }
            if (
              !shippingType?.includes(ShippingType.FCL) &&
              (!d.quantity || !d.volume || !d.weight)
            ) {
              error = ErrorTypes.EmptyCargoData;
            }
          });
        });
        setAddCargoError(error);
      }
      if (error) {
        isValid = false;
      }
    }

    if (selectedHSCodes.length <= 0) {
      setHsCodesError(ErrorTypes.RequiredField);
      isValid = false;
    } else {
      setHsCodesError(null);
    }

    if (!invalidData && isValid && onHandleContinueRouteComponent()) {
      setAddCargoError(null);
      setHsCodesError(null);
      setSelectedStep(2);
      return true;
    }

    return false;
  };

  const getDocumentsErrors = (): DocumentError[] => {
    const errors: DocumentError[] = [];
    const doc = documents as Document[];
    for (let i = 0; i < documents.length; i++) {
      if (doc[i].document.name.length >= fileNameSize) {
        errors.push({
          errorType: ErrorTypes.FileNameSizeExceded,
          index: i,
        });
      }
      if (Math.round(doc[i].document.size / 1048576) >= fileMaxSize) {
        errors.push({
          errorType: ErrorTypes.FileSizeExceded,
          index: i,
        });
      }
      if (doc.filter((x) => x.document.name === doc[i].document.name).length >= 2) {
        errors.push({ errorType: ErrorTypes.AlreadyExists, index: i });
      }
      if (!acceptedFileTypes.includes(doc[i].document.type)) {
        errors.push({ errorType: ErrorTypes.FileTypeNotSupported, index: i });
      }
      if (doc[i].doctype === undefined || doc[i].doctype === null || doc[i].doctype === "") {
        errors.push({
          errorType: ErrorTypes.MandatoryField,
          index: i,
        });
      }
    }
    return errors;
  };

  const onHandleContinueAddDocumentsComponent = (): boolean => {
    if (
      getDocumentsErrors().length <= 0 &&
      onHandleContinueRouteComponent() &&
      onHandleContinueAddCargoComponent()
    ) {
      setSelectedStep(3);
      return true;
    }
    setInvalidDocuments(true);
    return false;
  };

  const steps = [
    {
      title: i18n.t("LABEL_ROUTE"),
      onClick: () => {
        setSelectedStep(0);
      },
    },
    {
      title: i18n.t("LABEL_CARGO"),
      onClick: () => {
        onHandleContinueRouteComponent();
      },
    },
    {
      title: i18n.t("LABEL_DOCUMENTS"),
      onClick: () => {
        onHandleContinueAddCargoComponent();
      },
    },
    {
      title: i18n.t("LABEL_REVIEW"),
      onClick: () => {
        onHandleContinueAddDocumentsComponent();
      },
    },
  ];

  const onChangeUserType = (userType: UserType, partnerAddress: CompanyAddress | null) => {
    setUserType(userType);
    let shipper: PickPartnerChangeRegistration | null = null;
    let consignee: PickPartnerChangeRegistration | null = null;
    if (userType === UserType.Shipper) {
      shipper = {
        companyName: userProfile?.SelectedCompany?.FullName ?? "",
        address:
          userProfile?.SelectedCompany?.Address2 !== null
            ? userProfile?.SelectedCompany?.Address2 ?? ""
            : userProfile?.SelectedCompany?.Address3 ?? "",
        addressCode: partnerAddress?.AddressCode ?? "",
        postalCode: userProfile?.SelectedCompany?.PostCode ?? "",
        placeCity: {
          PointCode: partnerAddress?.PointCode ?? "",
          FullName: partnerAddress?.City ?? "",
          Country: partnerAddress?.CountryName ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          PortCode: partnerAddress?.PostCode ?? "",
          LocalName: "",
          PortType: props.freightMode === FreightMode.AI ? PortType.AIR : PortType.SEA,
          TimeZone: "",
          TimeZoneDiff: 0,
        },
        contact: userProfile?.User?.Username ?? "",
        email: userProfile?.Access?.Email ?? "",
        phone: userProfile?.User?.Phone ?? "",
        partner: {
          PartnerCode: partnerAddress?.PartnerCode ?? "",
          FullName: partnerAddress?.FullName ?? "",
          PartnerSubCode: partnerAddress?.PartnerSubCode ?? "",
          CompanyCode: partnerAddress?.CompanyCode ?? "",
          AddressType: partnerAddress?.AddressType ?? "",
          AddressCode: partnerAddress?.AddressCode ?? "",
          PointCode: partnerAddress?.PointCode ?? "",
          PostCode: partnerAddress?.PostCode ?? "",
          Address1: partnerAddress?.Address1 ?? "",
          Address2: partnerAddress?.Address2 ?? "",
          Address3: partnerAddress?.Address3 ?? "",
          Address4: partnerAddress?.Address4 ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          StateCode: partnerAddress?.StateCode ?? "",
          CountryName: partnerAddress?.CountryName ?? "",
          AddressTypeDescription: partnerAddress?.AddressTypeDescription ?? "",
          PointName: partnerAddress?.PointName ?? "",
          ContactNumber: userProfile?.User?.Phone ?? "",
          ContactName: userProfile?.User?.Username ?? "",
          ContactEmail: userProfile?.User?.SendEmail ?? "",
          City: partnerAddress?.City ?? "",
          Currency: "",
          EORINumber: partnerAddress?.EORINumber ?? "",
          VATNumber: partnerAddress?.VATNumber ?? "",
        },
      };
      consignee = {
        companyName: shipperData?.companyName ?? "",
        eoriNumber: shipperData?.eoriNumber,
        vatNumber: shipperData?.vatNumber,
        address: shipperData?.address ?? "",
        addressCode: shipperData?.addressCode ?? "",
        postalCode: shipperData?.postalCode ?? "",
        placeCity: shipperData?.placeCity ?? null,
        contact: shipperData?.contact ?? "",
        email: shipperData?.email ?? "",
        phone: shipperData?.phone ?? "",
        partner: shipperData?.partner ?? null,
      };

      setShipperData(shipper);
      setConsigneeData(consignee);
      consignee.placeCity?.CountryCode === "IS"
        ? setIsICS2Required(false)
        : setIsICS2Required(true);
    } else {
      setUserType(userType);
      shipper = {
        companyName: consigneeData?.companyName ?? "",
        eoriNumber: consigneeData?.eoriNumber,
        vatNumber: consigneeData?.vatNumber,
        address: consigneeData?.address ?? "",
        addressCode: consigneeData?.addressCode ?? "",
        postalCode: consigneeData?.postalCode ?? "",
        placeCity: consigneeData?.placeCity ?? null,
        contact: consigneeData?.contact ?? "",
        email: consigneeData?.email ?? "",
        phone: consigneeData?.phone ?? "",
        partner: consigneeData?.partner ?? null,
      };
      consignee = {
        companyName: userProfile?.SelectedCompany?.FullName ?? "",
        address:
          userProfile?.SelectedCompany?.Address2 !== null
            ? userProfile?.SelectedCompany?.Address2 ?? ""
            : userProfile?.SelectedCompany?.Address3 ?? "",
        addressCode: partnerAddress?.AddressCode ?? "",
        postalCode: userProfile?.SelectedCompany?.PostCode ?? "",
        placeCity: {
          PointCode: partnerAddress?.PointCode ?? "",
          FullName: partnerAddress?.City ?? "",
          Country: partnerAddress?.CountryName ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          PortCode: partnerAddress?.PostCode ?? "",
          LocalName: "",
          PortType: props.freightMode === FreightMode.AI ? PortType.AIR : PortType.SEA,
          TimeZone: "",
          TimeZoneDiff: 0,
        },
        contact: userProfile?.User?.Username ?? "",
        email: userProfile?.Access?.Email ?? "",
        phone: userProfile?.User?.Phone ?? "",
        partner: {
          PartnerCode: partnerAddress?.PartnerCode ?? "",
          FullName: partnerAddress?.FullName ?? "",
          PartnerSubCode: partnerAddress?.PartnerSubCode ?? "",
          CompanyCode: partnerAddress?.CompanyCode ?? "",
          AddressType: partnerAddress?.AddressType ?? "",
          AddressCode: partnerAddress?.AddressCode ?? "",
          PointCode: partnerAddress?.PointCode ?? "",
          PostCode: partnerAddress?.PostCode ?? "",
          Address1: partnerAddress?.Address1 ?? "",
          Address2: partnerAddress?.Address2 ?? "",
          Address3: partnerAddress?.Address3 ?? "",
          Address4: partnerAddress?.Address4 ?? "",
          CountryCode: partnerAddress?.CountryCode ?? "",
          StateCode: partnerAddress?.StateCode ?? "",
          CountryName: partnerAddress?.CountryName ?? "",
          AddressTypeDescription: partnerAddress?.AddressTypeDescription ?? "",
          PointName: partnerAddress?.PointName ?? "",
          ContactNumber: userProfile?.User?.Phone ?? "",
          ContactName: userProfile?.User?.Username ?? "",
          ContactEmail: userProfile?.User?.SendEmail ?? "",
          City: partnerAddress?.City ?? "",
          Currency: "",
          EORINumber: partnerAddress?.EORINumber ?? "",
          VATNumber: partnerAddress?.VATNumber ?? "",
        },
      };

      setConsigneeData(consignee);
      consignee.placeCity?.CountryCode === "IS"
        ? setIsICS2Required(false)
        : setIsICS2Required(true);
      setShipperData(shipper);
    }
    handlePortOfLoadOnShipperDataChange(shipper);
    handlePortOfDischargeOnConsigneeDataChange(consignee);
  };

  const onChangeFromData = (fromData: RequestCollectionRegistration) => {
    setFromData(fromData);
    setPortFrom(fromData.placeCity);
    if (fromData.placeCity != null) {
      CreateBookingService.getPortforPool(
        fromData.placeCity?.PointCode,
        "POL",
        props.freightMode
      ).then((port: Port) => {
        if (port && props.freightMode === FreightMode.SH) setPortOfLoad(port);
        else setPortOfLoad(fallbackPort);
      });
    }
  };

  const onChangeToData = (toData: RequestCollectionRegistration) => {
    setToData(toData);
    setPortTo(toData.placeCity);
    if (toData.placeCity != null) {
      CreateBookingService.getPortforPool(
        toData.placeCity?.PointCode,
        "POL",
        props.freightMode
      ).then((port: Port) => {
        if (port && props.freightMode === FreightMode.SH) setPortOfDischarge(port);
        else setPortOfDischarge(fallbackPort);
      });
    }
  };

  const onChangeCanCollectShipment = (canCollectShipment: boolean) => {
    setCanCollectShipment(canCollectShipment);
    setPolError(null);
  };

  const onChangeCanDeliverShipment = (canDeliverShipment: boolean) => {
    setCanDeliverShipment(canDeliverShipment);
    setPodError(null);
  };

  const onChangeCanSetDeliverToPort = (canSetDeliverToPort: boolean) => {
    setCanSetDeliverToPort(canSetDeliverToPort);
    setCollectionDetailsError(null);
  };

  const onChangeCanSetPickUpPort = (canSetPickUpPort: boolean) => {
    setCanSetPickUpPort(canSetPickUpPort);
    setDeliveryDetailsError(null);
  };

  const onChangeisSameAsShipper = (isSameAs: boolean) => {
    setIsSameAsShipper(isSameAs);
  };

  const onChangeisSameAsConsignee = (isSameAs: boolean) => {
    setIsSameAsConsignee(isSameAs);
  };

  return (
    <main className="flex flex-col items-center">
      <HeaderPage backgroundColour="gray" className="h-36 left-0 top-16 flex justify-center">
        <div className="flex-1 flex-col items-start overflow-hidden max-w-1024 top-24 bg-gray-100">
          <h1 className="static h-9 font-semibold text-2xl leading-9 px-6 flex items-center text-neutral-9 my-4 mt-6">
            {props.freightMode == FreightMode.SH
              ? t("CREATE_SEA_FREIGHT_BOOKING")
              : t("CREATE_AIR_FREIGHT_BOOKING")}
          </h1>
          <div className="pb-6 px-8 w-full h-full">
            <Stepper activeStep={selectedStep} steps={steps} />
          </div>
        </div>
      </HeaderPage>
      <Page className="md:m-6 justify-center w-375px md:w-1204px md:p-2">
        {selectedStep == 0 && (
          <RouteComponent
            billOfLading={billOfLading}
            billOfLadingError={billOfLadingError}
            buyerData={buyerData}
            buyerError={buyerError}
            canCollectShipment={canCollectShipment}
            canDeliverShipment={canDeliverShipment}
            canSetDeliverToPort={canSetDeliverToPort}
            canSetPickUpPort={canSetPickUpPort}
            collectionDetailsError={collectionDetailsError}
            companyAddresses={companyCustomerAddresses ?? []}
            consigneeData={consigneeData}
            consigneeError={consigneeError}
            deliveryDetailsError={deliveryDetailsError}
            filteredIncoterms={filteredIncoterms}
            fromData={fromData}
            incoterm={incoterm}
            incotermError={incotermError}
            incotermLocationError={incotermLocationError}
            incotermLocations={incotermLocations}
            isICS2Required={isICS2Required}
            isSameAsConsignee={isSameAsConsignee}
            isSameAsShipper={isSameAsShipper}
            loadingCompanyAddresses={loadingCompanyAddresses}
            loadingCompanyCustomerAddresses={loadingCompanyCustomerAddresses}
            loadingCopyBooking={loadingCopyBooking}
            loadingIncoterms={loadingIncoterms}
            notifierData={notifierData}
            onChangeBillOfLading={onChangeBillOfLading}
            onChangeBuyerData={onChangeBuyerData}
            onChangeCanCollectShipment={onChangeCanCollectShipment}
            onChangeCanDeliverShipment={onChangeCanDeliverShipment}
            onChangeCanSetDeliverToPort={onChangeCanSetDeliverToPort}
            onChangeCanSetPickUpPort={onChangeCanSetPickUpPort}
            onChangeConsigneeData={onChangeConsigneeData}
            onChangeFromData={onChangeFromData}
            onChangeIncoterm={onChangeIncoterm}
            onChangeIsSameAsConsignee={onChangeisSameAsConsignee}
            onChangeIsSameAsShipper={onChangeisSameAsShipper}
            onChangeNotifierData={onChangeNotifierData}
            onChangePortOfDischarge={onChangePortOfDischarge}
            onChangePortOfLoad={onChangePortOfLoad}
            onChangeQuoteNumber={onChangeQuoteNumber}
            onChangeSelectedIncotermLocation={onChangeSelectedIncotermLocation}
            onChangeSellerData={onChangeSellerData}
            onChangeShipperData={onChangeShipperData}
            onChangeToData={onChangeToData}
            onChangeUserType={onChangeUserType}
            onHandleContinue={() => onHandleContinueRouteComponent()}
            pageType={props.pageType}
            partnerAddress={partnerAddress}
            podError={podError}
            polError={polError}
            portOfDischarge={portOfDischarge}
            portOfLoad={portOfLoad}
            portsOfDischarge={portsOfDischarge ?? []}
            portsOfLoad={portsOfLoad ?? []}
            quoteNumber={quoteNumber}
            resetIncoterm={resetIncoterm}
            selectedIncotermLocation={selectedIncotermLocation}
            sellerData={sellerData}
            sellerError={sellerError}
            shipmentType={props.freightMode}
            shipperData={shipperData}
            shipperError={shipperError}
            toData={toData}
            updateIncotermPlaces={updateIncotermPlaces}
            userType={userType}
          />
        )}
        {selectedStep == 1 && (
          <CargoInformationComponent
            addCargoData={addCargoData}
            cargoLinesError={addCargoError}
            hsCodesError={hsCodesError}
            loadingCopyBooking={loadingCopyBooking}
            onChangeCargoData={onChangeCargoData}
            onChangeShippingType={onChangeShippingType}
            onDeleteCargoData={onDeleteCargoData}
            onEditCargoData={onEditCargoData}
            onHandleContinue={onHandleContinueAddCargoComponent}
            pageType={props.pageType}
            selectedHSCodes={onChangeSelectedHSCodes}
            selectedHSCodesList={selectedHSCodes}
            setSelectedTab={setSelectedStep}
            shipmentType={props.freightMode}
            shippingType={shippingType}
            shippingTypes={shippingTypes ?? []}
          />
        )}
        {selectedStep == 2 && (
          <AddDocumentsComponent
            documents={documents}
            freightMode={props.freightMode}
            getDocumentsErrors={getDocumentsErrors}
            invalidDocument={invalidDocuments}
            onChangeSelectedDocType={onChangeSelectedDocType}
            onDocumentAdded={onDocumentAdded}
            onDocumentDeleted={onDocumentDeleted}
            onHandleContinue={onHandleContinueAddDocumentsComponent}
            setSelectedTab={setSelectedStep}
          />
        )}
        {selectedStep == 3 && (
          <ReviewComponent
            addCargoData={addCargoData}
            billOfLading={
              userType === UserType.Shipper && props.freightMode === FreightMode.SH
                ? billOfLading?.label
                : bills.find((b) => b.value === "G")?.label
            }
            buyerData={buyerData}
            canCollectShipment={canCollectShipment}
            canDeliverShipment={canDeliverShipment}
            canSetDeliverToPort={canSetDeliverToPort}
            canSetPickUpPort={canSetPickUpPort}
            consigneeData={consigneeData}
            documents={documents}
            freightMode={props.freightMode}
            fromData={fromData}
            handleOnCreateBooking={handleOnCreateBooking}
            hsCodes={selectedHSCodes}
            incoterm={incoterm}
            incotermLocation={selectedIncotermLocation}
            loadingCreateBooking={loadingCreateBooking}
            notifierData={notifierData}
            portOfDischarge={portOfDischarge}
            portOfLoad={portOfLoad}
            quoteNumber={quoteNumber}
            sellerData={sellerData}
            setSelectedTab={setSelectedStep}
            shipperData={shipperData}
            shippingType={shippingType}
            toData={toData}
            userProfile={userProfile}
          />
        )}
      </Page>
    </main>
  );
};

export default CreateTrace;
